// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngSticky from "@flip/App/Components/XngSticky";
import { DivWrapper } from "@flip/XDL/constants";
import { mergeWithCss, xScreen } from "@flip/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useMemo, useState } from "react";
import { css } from "styled-components";

interface CartPageTemplateProps {
    children: React.ReactNode;
    extendedStyles?: CartPageTemplateStyles;
    isReview?: boolean;
}

interface Props {
    MainSection: React.FC<any>;
    HeaderSection: React.FC<any>;
    CartApproverMessage: React.FC<any>;
    CartFulfillmentSelector: React.FC<any>;
    CartTotalSection: React.FC<any>;
    ActionsSection: React.FC<any>;
}

interface CartPageTemplateContextProps {
    styles: CartPageTemplateStyles;
    isReview?: boolean;
}

export interface CartPageTemplateStyles {
    container?: InjectableCss;
    headerSection?: InjectableCss;
    mainSection?: InjectableCss;
    cartApproverMessage?: InjectableCss;
    cartFulfullmentSelector?: InjectableCss;
    totalSection?: InjectableCss;
    actionsSection?: InjectableCss;

    /* XNGAGE Changes */
    xngStickyStyles?: InjectableCss;
}

const CartPageTemplateContext = React.createContext<CartPageTemplateContextProps>({
    styles: {},
    isReview: false,
});

export const useCartPageTemplateContext = () => {
    const context = React.useContext(CartPageTemplateContext);
    if (!context) {
        throw new Error("CartPageTemplate compound components cannot be rendered outside CartPageTemplate");
    }
    return context;
};

export const cartPageTemplateBreakpoint = 767;
export const cartPageTemplateStyles: CartPageTemplateStyles = {
    container: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            margin-bottom: 64px;
            @media print {
                display: block;
            }
        `,
    },
    headerSection: {
        css: css`
            width: 100%;
        `,
    },
    actionsSection: {
        css: css`
            padding-right: 22px;
            width: calc(100% - 319px);

            ${xScreen("lte", "md")} {
                padding-right: 20px;
                width: calc(100% - 234px);
            }

            ${xScreen("lt", cartPageTemplateBreakpoint)} {
                padding-right: 0;
                order: 4;
                width: 100%;
            }
        `,
    },
    mainSection: {
        css: css`
            padding-right: 22px;
            width: calc(100% - 319px);

            ${xScreen("lte", "md")} {
                padding-right: 20px;
                width: calc(100% - 234px);
            }

            ${xScreen("lte", cartPageTemplateBreakpoint)} {
                width: 100%;
                padding: 0;
            }
            /* Start XNGAGE Changes */
            @media print {
                width: 100%;
            }
            /* END XNGAGE Changes */
        `,
    },
    totalSection: {
        css: css`
            width: 319px;

            [data-test-selector="PageTitle"] {
                display: none;
            }

            ${xScreen("lte", "md")} {
                width: 232px;
            }

            ${xScreen("lte", cartPageTemplateBreakpoint)} {
                width: 100%;
                display: flex;
                justify-content: center;
                order: -1;

                & > div.xng-sticky {
                    max-width: 100%;
                }

                & > div.xng-sticky {
                    max-width: 300px;
                }

                [data-test-selector="PageTitle"] {
                    display: block;
                }
            }

            ${xScreen("lte", "xs")} {
                & > div.xng-sticky {
                    max-width: 100%;
                }
            }

            /* XNG-Changes: FLIP-975 */
            @media print {
                width: 100%;
                & > div.xng-sticky {
                    position: static;
                    max-width: 100%;
                }
            }

            ${xScreen("lte", "sm")} {
                ${({ isReview }: any) => {
                    if (isReview) {
                        return css`
                            display: none;
                        `;
                    }
                }}
            }
        `,
    },
    /* Start XNGAGE Changes */
    xngStickyStyles: {
        css: css`
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    /* END XNGAGE Changes */
};

const CartPageTemplate: FC<CartPageTemplateProps> & Props = ({ children, extendedStyles, isReview }) => {
    const [styles] = useState(() => mergeWithCss(cartPageTemplateStyles, extendedStyles));
    const value = useMemo(() => ({ styles, isReview }), [styles, isReview]);
    return (
        <CartPageTemplateContext.Provider value={value}>
            <StyledWrapper {...styles.container}>{children}</StyledWrapper>
        </CartPageTemplateContext.Provider>
    );
};

const HeaderSection: FC = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return (
        <StyledWrapper {...styles.headerSection} className="cart-header-section">
            {children}
        </StyledWrapper>
    );
};

const CartApproverMessage: FC = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.cartApproverMessage}>{children}</StyledWrapper>;
};
const CartFulfillmentSelector: FC = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.cartFulfullmentSelector}>{children}</StyledWrapper>;
};
const MainSection: FC = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.mainSection}>{children}</StyledWrapper>;
};

const CartTotalSection: FC = ({ children }) => {
    const { styles, isReview } = useCartPageTemplateContext();

    return (
        <StyledWrapper {...styles.totalSection} {...{ isReview }}>
            <XngSticky {...styles.xngStickyStyles /* XNGAGE Changes */}>{children}</XngSticky>
        </StyledWrapper>
    );
};

const ActionsSection: FC = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.actionsSection}>{children}</StyledWrapper>;
};

CartPageTemplate.HeaderSection = HeaderSection;
CartPageTemplate.MainSection = MainSection;
CartPageTemplate.CartApproverMessage = CartApproverMessage;
CartPageTemplate.CartFulfillmentSelector = CartFulfillmentSelector;
CartPageTemplate.CartTotalSection = CartTotalSection;
CartPageTemplate.ActionsSection = ActionsSection;

export default CartPageTemplate;
